import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ConfirmChangesDialog from './ConfirmChangesDialog';

const BROWSER_BACK = '__browser_back__';

// Pushes a dummy history entry so clicking "back" pops it instead of leaving the page
const pushHistoryGuard = () => window.history.pushState(null, '');

const NavigationBlocker = ({ isBlocking = true, onNavigate = () => {} }) => {
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const guardActive = useRef(false);

    // Browser-level blocking: back button (popstate) and page refresh (beforeunload)
    useEffect(() => {
        if (!isBlocking) return undefined;

        if (!guardActive.current) {
            pushHistoryGuard();
            guardActive.current = true;
        }

        // Shows native browser dialog on refresh/tab close (custom dialogs are not possible)
        const handleBeforeUnload = e => e.preventDefault();

        // Guard entry was popped — user clicked the browser back button
        const handlePopState = () => {
            guardActive.current = false;
            setPendingNavigation(BROWSER_BACK);
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isBlocking]);

    // Intercept hash-based navigation clicks in the capture phase before the router processes them
    useEffect(() => {
        const handleClick = e => {
            const link = e.target.closest('a[href^="#"]');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href || href.length <= 1 || href === window.location.hash) return;

            if (!isBlocking) {
                onNavigate();
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            setPendingNavigation(href);
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [isBlocking, onNavigate]);

    const handleAccept = () => {
        setPendingNavigation(null);

        if (pendingNavigation === BROWSER_BACK) {
            window.history.back();
        } else {
            window.location.href = pendingNavigation;
        }

        onNavigate();
    };

    const handleReject = () => {
        setPendingNavigation(null);

        // Re-push guard entry so the back button is blocked again
        if (pendingNavigation === BROWSER_BACK) {
            pushHistoryGuard();
            guardActive.current = true;
        }
    };

    return <ConfirmChangesDialog open={!!pendingNavigation} onAccept={handleAccept} onReject={handleReject} />;
};

NavigationBlocker.propTypes = {
    isBlocking: PropTypes.bool,
    onNavigate: PropTypes.func,
};

export default NavigationBlocker;
